import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import {
  DataApiResponse,
  DataApiResponseLegacy,
  EnvConfig,
  POS,
  POSTerminal,
  PaymentMethod,
  PosPrintSettings,
  PrinterAssignmentInput,
  SeatQrCode,
  SeatQrCodeBulkCreate,
  SeatQrCodeBulkRemap,
  SeatQrCodeListItem,
  SeatQrCodesQuery,
} from '@shared/definitions';

@Injectable({
  providedIn: 'root',
})
export class POSApiService {
  readonly posBaseUrl = '/api/v1.0/pos';
  readonly pointOfSalesBaseUrl = '/api/v1.0/point-of-sales';
  // Seat QR codes are addressed flat, not under a POS — see getSeatQrCodes.
  readonly seatQrCodesBaseUrl = '/api/v1.0/seat-qr-codes';

  constructor(
    private http: HttpClient,
    @Inject(ENV_CONFIG) private envConfig: EnvConfig
  ) {}

  getPOSList = (params) => this.http.get<DataApiResponse<POS[]>>(this.posBaseUrl, { params });

  getPOS = (posId: string) => this.http.get<POS>(`${this.posBaseUrl}/${posId}`);

  addPOS = (pos: any) => this.http.post(this.posBaseUrl, pos);

  editPOS = (pos: any) => this.http.put(this.posBaseUrl, pos);

  removePOS = (posId: string) => this.http.delete(`${this.posBaseUrl}/${posId}`);

  activatePOS = (posId: string) => this.http.post<POS>(`${this.posBaseUrl}/${posId}/activate`, {});

  deactivatePOS = (posId: string) => this.http.post<POS>(`${this.posBaseUrl}/${posId}/deactivate`, {});

  getPOSTerminals = (posId: string) => this.http.get<POSTerminal[]>(`${this.posBaseUrl}/${posId}/terminals`);

  /** PAUL-2893: every method the shop's integrations offer for this POS type, activated or not - the
   *  activation picker set. Staff only (Moderator+ in the POS's company); 401 anonymous, 403 otherwise. */
  getAllPOSPaymentMethods = (posId: string) =>
    this.http.get<PaymentMethod[]>(`${this.posBaseUrl}/${posId}/payment-methods/all`);

  /** PAUL-2893: only the methods activated on the POS, in the POS's order - what a buyer can pay with.
   *  The same list for every caller type; anonymous. */
  getAvailablePOSPaymentMethods = (posId: string) =>
    this.http.get<PaymentMethod[]>(`${this.posBaseUrl}/${posId}/payment-methods/available`);

  /** @deprecated PAUL-2893 - returns the picker set for dashboard logins and the activated set for everyone
   *  else, decided by the login source. Use getAllPOSPaymentMethods (picker) or
   *  getAvailablePOSPaymentMethods (buyer). The backend removes the endpoint after 2026-12-07. */
  getPOSPaymentMethods = (posId: string) => this.http.get(`${this.posBaseUrl}/${posId}/payment-methods`);

  getProductsPaymentMethods(params: { integration_type: 'all-in' | 'pay' | 'mollie' } & any) {
    return this.http.get<PaymentMethod[]>(`${this.posBaseUrl}/payment-methods`, { params });
  }
  // Steps

  updatePOSGeneralInformation = (pos: any) => this.http.put(`${this.posBaseUrl}/general-info`, pos);

  updatePOSPurchaseFlow = (pos: any) => this.http.put(`${this.posBaseUrl}/purchase-flow`, pos);

  updatePOSPaymentMethods = (pos: any) => this.http.put(`${this.posBaseUrl}/payment-methods`, pos);

  updatePOSTerminal = (pos: Pick<POS, 'id' | 'payment_terminal_external_id'>) =>
    this.http.put<POS>(`${this.posBaseUrl}/payment-terminal`, pos);

  updatePOSDNSConfig = (pos: any) => this.http.put(`${this.posBaseUrl}/dns-config`, pos);

  updatePOSProducts = (pos: any) => this.http.put(`${this.posBaseUrl}/products`, pos);

  updatePOSAddOns = (pos: any) => this.http.put(`${this.posBaseUrl}/addons`, pos);

  /* PAUL-3461: full replace, like products and add-ons — a call that omits or empties discount_ids
     unlinks every discount from this POS. Moderator and above only; below that it is a 403. */
  updatePOSDiscounts = (pos: any) => this.http.put<POS>(`${this.posBaseUrl}/discounts`, pos);

  updatePOSFnbInfo = (pos: any) => this.http.put<POS>(`${this.posBaseUrl}/fnb-info`, pos);

  updatePOSKitchenDisplayAreas = (pos: any) => this.http.put<POS>(`${this.posBaseUrl}/kitchen-display-areas`, pos);

  bulkCreateSeatQrCodes = (posId: string, body: SeatQrCodeBulkCreate) =>
    this.http.post<SeatQrCode[]>(`${this.posBaseUrl}/${posId}/seat-qr-codes/bulk`, body);

  /* Flat rather than nested under a POS: `pos` is a filter here, and a code with no POS at all would be
     unreachable under a POS-scoped route — those are exactly the codes the screen exists to find. */
  getSeatQrCodes = (params: SeatQrCodesQuery) =>
    this.http.get<DataApiResponseLegacy<SeatQrCodeListItem[]>>(this.seatQrCodesBaseUrl, {
      // Typed for callers, then widened: HttpClient's params take an index signature, which an interface
      // of optional properties is not assignable to. Callers build the object with unset keys omitted
      // rather than undefined, so nothing lands on the query string as "undefined".
      params: { ...params } as Record<string, string | number | boolean>,
    });

  /* A URL rather than a request: the hover preview renders it straight into an <img>, so it is the browser
     that fetches it. Built here anyway so the service stays the only place that knows these paths.
     Absolute, and the only URL in this service that is: a native image load never passes through
     ApiInterceptor, so nothing would rewrite the /api prefix to the API host on a deployed dashboard.
     Relative, the browser asks the Angular host instead, whose SPA fallback answers every unmatched path
     with index.html - a 200 of text/html, which an <img> renders as an empty box. Same shape as
     `${envConfig.api}/external/pos/...` in pos-overview. The /api strip mirrors ApiInterceptor.setProxy so
     the path above stays the single source of truth. */
  seatQrCodePngUrl = (seatQrCodeId: string, size = 240) =>
    `${this.envConfig.api}${this.seatQrCodesBaseUrl.replace('/api', '')}/${seatQrCodeId}/qr.png?size=${size}`;

  bulkRemapSeatQrCodes = (body: SeatQrCodeBulkRemap) =>
    this.http.put<SeatQrCode[]>(`${this.seatQrCodesBaseUrl}/remap`, body);

  /* PAUL-3658: puts a spare sticker's code on a seat. Changed in place, so the response is the same row (same
     id) carrying the new code. */
  editSeatQrCode = (id: string, body: { code: string }) =>
    this.http.put<SeatQrCode>(`${this.seatQrCodesBaseUrl}/${id}/code`, body);

  getSeatQrCodesPdf = (posId: string, areaId: string) =>
    this.http.get(`${this.posBaseUrl}/${posId}/qr-codes.pdf`, { params: { area: areaId }, responseType: 'blob' });

  printSampleReceipt = (posId: string) => this.http.post<POS>(`${this.posBaseUrl}/${posId}/print-sample-receipt`, {});

  getPrintSettings = (posId: string) => this.http.get<PosPrintSettings>(`${this.posBaseUrl}/${posId}/print-settings`);

  // PAUL-3128: has_printer travels with the assignment set — the endpoint is a full replace, so a call
  // that omits the flag clears it.
  setPrinterAssignments = (posId: string, hasPrinter: boolean, assignments: PrinterAssignmentInput[]) =>
    this.http.put(`${this.pointOfSalesBaseUrl}/${posId}/printer-assignments`, {
      has_printer: hasPrinter,
      assignments,
    });
}
